import { useIntl } from 'react-intl';

import { Actions } from '~/eds/src/components/Actions/Actions';
import { DescriptionText } from '~/eds/src/components/DescriptionText/DescriptionText';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Stepper } from '~/eds/src/components/Stepper/Stepper';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

import { OptimizeState } from '../../../../../redux/api/types/sylvanus/model';

interface Props {
  state: OptimizeState;
  progressComplete: number;
}

export const BoostProgress = ({ state, progressComplete }: Props) => {
  const intl = useIntl();
  const { navigate } = useRouting();

  const steps = [
    {
      id: 'rewiewing',
      isLoading: state === 'initializing',
      label: intl.formatMessage({
        id: 'BoostProgress.reviewingFeedback',
        defaultMessage: 'Reviewing your feedback',
        description: 'Title for reviewing feedback step',
      }),
      description: intl.formatMessage({
        id: 'BoostProgress.reviewingFeedbackDescription',
        defaultMessage:
          'Our AI is considering your ratings and reviewing the feedback you provided on incorrect values.',
        description: 'Description for reviewing feedback step',
      }),
      isCompleted: state === 'running',
    },
    {
      id: 'testing',
      isLoading:
        state === 'running' && progressComplete >= 0 && progressComplete < 0.33,
      label: intl.formatMessage({
        id: 'BoostProgress.testingImprovements',
        defaultMessage: 'Testing model improvements',
        description: 'Title for testing improvements step',
      }),
      description: intl.formatMessage({
        id: 'BoostProgress.testingImprovementsDescription',
        defaultMessage:
          'Our AI is checking for optimizations to your model configurations.',
        description: 'Description for testing improvements step',
      }),
      isCompleted: progressComplete >= 0.33,
    },
    {
      id: 'writing',
      isLoading: progressComplete >= 0.33 && progressComplete < 0.67,
      label: intl.formatMessage({
        id: 'BoostProgress.updatingInstructions',
        defaultMessage: 'Updating instructions',
        description: 'Title for updating instructions step',
      }),
      description: intl.formatMessage({
        id: 'BoostProgress.updatingInstructionsDescription',
        defaultMessage:
          'Our AI is drafting new instructions that incorporate your ratings and feedback.',
        description: 'Description for updating instructions step',
      }),
      isCompleted: progressComplete >= 0.67,
    },
    {
      id: 'analyzing',
      isLoading: progressComplete >= 0.67,
      label: intl.formatMessage({
        id: 'BoostProgress.analyzingAccuracy',
        defaultMessage: 'Analyzing model accuracy',
        description: 'Title for analyzing accuracy step',
      }),
      description: intl.formatMessage({
        id: 'BoostProgress.analyzingAccuracyDescription',
        defaultMessage:
          'Our AI is reviewing multiple optimized versions of the model to recommend the best ones to you.',
        description: 'Description for analyzing accuracy step',
      }),
      isCompleted: state === 'done' && progressComplete === 1,
    },
  ];

  return (
    <Layout
      align="center"
      justify="center"
      w="540"
      preset="xl"
      direction="column"
      m="auto"
    >
      <DescriptionText
        text={intl.formatMessage({
          id: 'BoostProgress.boostDescription',
          defaultMessage:
            'Our AI is reviewing your ratings and testing improvements to increase your accuracy. This could take several minutes. AI Boost will continue to run if you leave this page.',
          description: 'Description text for boost progress',
        })}
      />
      <Stepper isOrdered={true} name="boost progression" steps={steps} />
      <Actions
        direction="row"
        actions={[
          {
            text: intl.formatMessage({
              id: 'BoostProgress.returnToAutomationHub',
              defaultMessage: 'Return to Automation Hub',
              description: 'Button text to return to automation hub',
            }),
            level: 'secondary',
            onClick: () => navigate(RoutePathType.AutomationHubFields),
          },
        ]}
      />
    </Layout>
  );
};
